import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Building2, Package, ClipboardList, Truck, Inbox, User, X } from "lucide-react";
import { api } from "@/lib/api";

const TYPES = [
  { key: "companies",    label: "Companies",      icon: Building2,     route: (r) => `/companies/${r.id}` },
  { key: "contacts",     label: "Contacts",       icon: User,          route: (r) => `/companies/${r.Company_ID}` },
  { key: "inventory",    label: "Products",       icon: Package,       route: () => `/inventory` },
  { key: "orders",       label: "Orders",         icon: ClipboardList, route: (r) => `/orders/${r.id}` },
  { key: "dockets",      label: "Dockets",        icon: Truck,         route: () => `/dockets` },
  { key: "purchase_orders", label: "Purchase Orders", icon: Inbox,    route: () => `/purchase-orders` },
];

const HAYSTACKS = {
  companies: (c) => [c.Company_Name, c.Email, c.Phone, c.POCity, c.ABN].filter(Boolean).join(" ").toLowerCase(),
  contacts:  (c) => [c.First_Name, c.Last_Name, c.Email, c.Phone, c.Job_Title].filter(Boolean).join(" ").toLowerCase(),
  inventory: (p) => [p.Product_Name, p.SKU, p.Category, p.Colour].filter(Boolean).join(" ").toLowerCase(),
  orders:    (o) => [o.id?.slice(0, 8), o.Delivery_Address, o.Notes, o.Order_Status].filter(Boolean).join(" ").toLowerCase(),
  dockets:   (d) => [d.Docket_Number, d.Delivery_Address, d.Docket_Status].filter(Boolean).join(" ").toLowerCase(),
  purchase_orders: (p) => [p.id?.slice(0, 8), p.Parsed_Data?.customer_name, p.Parsed_Data?.po_reference].filter(Boolean).join(" ").toLowerCase(),
};

const LABEL_OF = {
  companies: (c) => c.Company_Name,
  contacts:  (c) => `${c.First_Name} ${c.Last_Name}`,
  inventory: (p) => p.Product_Name,
  orders:    (o) => `Order ${o.id?.slice(0,8)} · ${o.Order_Status}`,
  dockets:   (d) => `${d.Docket_Number || "Docket"} · ${d.Docket_Status}`,
  purchase_orders: (p) => `PO ${p.id?.slice(0,8)} · ${p.Parsed_Data?.customer_name || "—"}`,
};

const SUBLABEL_OF = {
  companies: (c) => `${c.Account_Tier} · ${c.Status}`,
  contacts:  (c) => c.Email,
  inventory: (p) => [p.SKU, p.Colour].filter(Boolean).join(" · "),
  orders:    (o) => o.Delivery_Address || "",
  dockets:   (d) => d.Delivery_Address || "",
  purchase_orders: (p) => p.Parsed_Data?.delivery_address || "",
};

export default function GlobalSearch() {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const wrapRef = useRef(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);

  // Cmd/Ctrl + K to focus
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
      if (e.key === "Escape") {
        setOpen(false);
        inputRef.current?.blur();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Click outside closes
  useEffect(() => {
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // Lazy load data first time the user opens search
  useEffect(() => {
    if (open && !data) {
      Promise.allSettled([
        api.get("/companies"),
        api.get("/contacts"),
        api.get("/inventory"),
        api.get("/orders"),
        api.get("/dockets"),
        api.get("/purchase-orders").catch(() => ({ data: [] })),
      ]).then(([c, ct, p, o, d, po]) => {
        setData({
          companies: c.value?.data || [],
          contacts:  ct.value?.data || [],
          inventory: p.value?.data || [],
          orders:    o.value?.data || [],
          dockets:   d.value?.data || [],
          purchase_orders: po.value?.data || [],
        });
      });
    }
  }, [open, data]);

  const results = useMemo(() => {
    if (!data || !q.trim()) return [];
    const needle = q.trim().toLowerCase();
    const out = [];
    for (const t of TYPES) {
      const rows = (data[t.key] || [])
        .filter((r) => HAYSTACKS[t.key](r).includes(needle))
        .slice(0, 4)
        .map((r) => ({ type: t, row: r }));
      out.push(...rows);
    }
    return out.slice(0, 16);
  }, [data, q]);

  useEffect(() => { setActiveIdx(0); }, [q]);

  const go = (entry) => {
    setOpen(false);
    setQ("");
    navigate(entry.type.route(entry.row));
  };

  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter" && results[activeIdx]) { e.preventDefault(); go(results[activeIdx]); }
  };

  const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

  return (
    <div ref={wrapRef} className="relative w-96 max-w-[60vw]" data-testid="global-search">
      <div className="relative">
        <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          ref={inputRef}
          data-testid="global-search-input"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder="Search everything…"
          className="h-9 w-full pl-9 pr-16 text-sm bg-white border border-zinc-200 rounded-lg focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
        />
        <kbd className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] font-mono text-zinc-500 bg-zinc-100 border border-zinc-200 px-1.5 py-0.5 rounded">
          {isMac ? "⌘K" : "Ctrl+K"}
        </kbd>
      </div>

      {open && q.trim() && (
        <div data-testid="global-search-results" className="absolute top-full mt-2 w-[36rem] max-w-[80vw] bg-white border border-zinc-200 rounded-xl shadow-xl py-2 z-50 max-h-[60vh] overflow-y-auto">
          {!data && <div className="px-4 py-6 text-center text-xs text-zinc-500">Loading index…</div>}
          {data && results.length === 0 && (
            <div className="px-4 py-6 text-center text-sm text-zinc-500">
              No results for "<span className="font-semibold text-zinc-700">{q}</span>".
            </div>
          )}
          {results.map((entry, idx) => {
            const Icon = entry.type.icon;
            const active = idx === activeIdx;
            return (
              <button
                key={`${entry.type.key}-${entry.row.id}-${idx}`}
                onClick={() => go(entry)}
                onMouseEnter={() => setActiveIdx(idx)}
                data-testid={`global-search-result-${idx}`}
                className={["w-full text-left flex items-center gap-3 px-3 py-2 mx-1 rounded-lg transition-colors", active ? "bg-brand-50" : "hover:bg-zinc-50"].join(" ")}
              >
                <div className={["w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0", active ? "bg-brand-100 text-brand-700" : "bg-zinc-100 text-zinc-600"].join(" ")}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-zinc-900 truncate">{LABEL_OF[entry.type.key](entry.row)}</div>
                  <div className="text-xs text-zinc-500 truncate">{entry.type.label} · {SUBLABEL_OF[entry.type.key](entry.row) || ""}</div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
